import type { AdminItem, AdminOffer, LocalizedPair, OfferType, ScheduleState } from '@diora/contracts';
import { editablePair } from '@diora/contracts';
import { discountPercent, formatDateTime } from '@diora/contracts';
import { useQuery } from '@tanstack/react-query';
import { Plus, Tag, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/app-shell';
import { Money } from '@/components/money';
import { BilingualField, OptionalBilingualField } from '@/components/bilingual';
import { FormPage, RecordPage, useDirty, useRecord } from '@/components/form-page';
import { ImagePicker } from '@/components/image-picker';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  EmptyState,
  Field,
  Input,
  Pagination,
  Select,
  SkeletonRows,
  Toolbar,
} from '@/components/ui';
import { RowActions, StatusBadge } from '@/components/records';
import {
  apiGet,
  apiPatch,
  apiPost,
  apiPut,
  useAdminList,
  useAdminMutation,
  useLifecycle,
  useListParams,
} from '@/hooks/admin';
import { qk } from '@/lib/query';
import { useAuth } from '@/providers/auth';
import { useLocale } from '@/providers/locale';
import { ListFilters } from './_filters';

const URL = '/admin/offers';
/** The list this screen's form pages return to. */
const LIST = '/offers';
const INVALIDATE = [['offers'], ['offer'], qk.dashboard];
const STATES: ScheduleState[] = ['upcoming', 'active', 'expired'];

export function OffersPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');

  const [params, setParams] = useListParams();
  const [state, setState] = useState<string>('');
  const query = { ...params, ...(state ? { scheduleState: state } : {}) };

  const list = useAdminList<AdminOffer>(qk.offers(query), URL, query);
  const lifecycle = useLifecycle(URL, INVALIDATE);

  return (
    <>
      <PageHeader
        title={t('offers.title')}
        description={t('offers.subtitle')}
        actions={
          canEdit && (
            <Link to={`${LIST}/new`} className="dio-btn dio-btn--primary">
              <Plus className="dio-icon" aria-hidden="true" />
              {t('offers.newTitle')}
            </Link>
          )
        }
      />

      <Toolbar>
        <ListFilters params={params} onChange={setParams}>
          <Select
            value={state}
            aria-label={t('offers.filterState')}
            onChange={(event) => {
              setState(event.target.value);
              setParams({ page: 1 });
            }}
          >
            <option value="">{t('common.all')}</option>
            {STATES.map((option) => (
              <option key={option} value={option}>
                {t(`status.${option}`)}
              </option>
            ))}
          </Select>
        </ListFilters>
      </Toolbar>

      {list.isLoading ? (
        <SkeletonRows />
      ) : list.rows.length === 0 ? (
        <EmptyState icon={<Tag aria-hidden="true" />} title={t('offers.empty')} hint={t('offers.subtitle')} />
      ) : (
        <DataTable
          caption={t('offers.title')}
          columns={[
            <span className="sr-only">{t('common.image')}</span>,
            t('offers.offerTitle'),
            t('offers.window'),
            t('offers.items'),
            t('common.status'),
            <span className="sr-only">{t('common.actions')}</span>,
          ]}
        >
          {list.rows.map((row) => (
            <tr key={row.id}>
              <td style={{ inlineSize: '1%' }}>
                {row.image ? (
                  <img src={row.image.thumbUrl} alt="" className="dio-table__thumb" />
                ) : (
                  <span className="dio-table__thumb" aria-hidden="true" />
                )}
              </td>

              <td>
                <div className="dio-table__name flex flex-wrap items-center gap-2">
                  <span lang="ar" dir="rtl">
                    {row.title.ar || '—'}
                  </span>
                  <span className="dio-meta" aria-hidden="true">
                    /
                  </span>
                  <span lang="en" dir="ltr">
                    {row.title.en || '—'}
                  </span>
                </div>
                <Badge tone="outline">{t(`offers.${row.offerType}`)}</Badge>
              </td>

              {/*
                A window is a range of two formatted dates, and `Intl` builds an
                Arabic one out of right-to-left marks. Forcing the cell to LTR
                would scramble it — that bug shipped on all three surfaces once.
                The range only needs its reading order pinned, which `tabular`
                and the row's own direction already give it.
              */}
              <td className="dio-table__sub tabular">
                {row.startsAt ? formatDateTime(row.startsAt, locale) : t('common.notSet')}
                {' → '}
                {row.endsAt ? formatDateTime(row.endsAt, locale) : t('common.notSet')}
              </td>

              <td className="dio-table__sub">
                <span className="tabular">{row.items.length}</span>
                {row.items.some((item) => !item.publiclyVisible) && (
                  <>
                    {' · '}
                    <span style={{ color: 'var(--color-warning-fg)' }}>{t('offers.notVisible')}</span>
                  </>
                )}
              </td>

              <td>
                <div className="flex flex-wrap items-center gap-1">
                  <StatusBadge status={row.publicationStatus} />
                  <Badge
                    tone={
                      row.scheduleState === 'active'
                        ? 'success'
                        : row.scheduleState === 'upcoming'
                          ? 'info'
                          : 'neutral'
                    }
                    dot
                  >
                    {t(`status.${row.scheduleState}`)}
                  </Badge>
                </div>
              </td>

              <td className="dio-table__actions">
                <div className="dio-actions">
                  <Link to={`${LIST}/${row.id}`} className="dio-btn dio-btn--secondary dio-btn--sm">
                    {canEdit ? t('common.edit') : t('common.details')}
                  </Link>
                  <RowActions
                    status={row.publicationStatus}
                    canEdit={canEdit}
                    busy={lifecycle.busy}
                    label={t('common.openMenuFor', { name: row.title.ar || row.title.en })}
                    handlers={{
                      onPublish: () => lifecycle.publish.mutate({ id: row.id, version: row.version }),
                      onUnpublish: () => lifecycle.unpublish.mutate({ id: row.id, version: row.version }),
                      onArchive: () => lifecycle.archive.mutate({ id: row.id, version: row.version }),
                      onRestore: () => lifecycle.restore.mutate({ id: row.id, version: row.version }),
                    }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </DataTable>
      )}

      <Pagination
        page={params.page ?? 1}
        pageSize={params.pageSize ?? 20}
        total={list.total}
        onPage={(page) => setParams({ page })}
      />

    </>
  );
}

/** `datetime-local` wants "YYYY-MM-DDTHH:mm" with no zone or seconds. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export function OfferFormPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');

  const record = useRecord<AdminOffer>(qk.offer(id ?? ''), URL, id);

  if (!id) return <OfferForm offer={null} canEdit={canEdit} />;

  return (
    <RecordPage title={t('offers.editTitle')} backTo={LIST} record={record}>
      {(row) => <OfferForm key={row.id} offer={row} canEdit={canEdit} />}
    </RecordPage>
  );
}

function OfferForm({ offer, canEdit }: { offer: AdminOffer | null; canEdit: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [title, setTitle] = useState<LocalizedPair>(offer?.title ?? { ar: '', en: '' });
  const [description, setDescription] = useState<LocalizedPair>(editablePair(offer?.description));
  const [terms, setTerms] = useState<LocalizedPair>(editablePair(offer?.terms));
  const [offerType, setOfferType] = useState<OfferType>(offer?.offerType ?? 'ITEM_DISCOUNT');
  const [startsAt, setStartsAt] = useState(toLocalInput(offer?.startsAt ?? null));
  const [endsAt, setEndsAt] = useState(toLocalInput(offer?.endsAt ?? null));
  const [imageId, setImageId] = useState<string | null>(offer?.image?.id ?? null);
  const [image, setImage] = useState(offer?.image ?? null);

  const typeChanged = offer !== null && offerType !== offer.offerType;
  const dirty = useDirty({ title, description, terms, offerType, startsAt, endsAt, imageId });

  const save = useAdminMutation(
    () => {
      const body = {
        titleAr: title.ar,
        titleEn: title.en,
        descriptionAr: description.ar,
        descriptionEn: description.en,
        termsAr: terms.ar,
        termsEn: terms.en,
        offerType,
        startsAt: fromLocalInput(startsAt),
        endsAt: fromLocalInput(endsAt),
        imageId,
      };
      return offer
        ? apiPatch(`${URL}/${offer.id}`, {
            ...body,
            expectedVersion: offer.version,
            // The server refuses a type change that would drop items until the
            // caller has said they know it will.
            ...(typeChanged ? { confirmTypeChange: true } : {}),
          })
        : apiPost(URL, body);
    },
    {
      successMessage: offer ? t('common.saved') : t('common.created'),
      invalidate: INVALIDATE,
      onSuccess: () => navigate(LIST),
    },
  );

  return (
    <FormPage
      title={offer ? t('offers.editTitle') : t('offers.newTitle')}
      description={t('offers.subtitle')}
      backTo={LIST}
      dirty={dirty}
      saving={save.isPending}
      canSave={title.ar.trim().length > 0}
      onSave={canEdit ? () => save.mutate(undefined) : undefined}
    >
      <BilingualField
        label={t('offers.offerTitle')}
        value={title}
        onChange={setTitle}
        required
        maxLength={140}
        disabled={!canEdit}
      />

      <Field label={t('offers.type')} htmlFor="offer-type" hint={t('offers.typeHint')}>
        <Select
          id="offer-type"
          value={offerType}
          disabled={!canEdit}
          onChange={(event) => setOfferType(event.target.value as OfferType)}
        >
          <option value="ITEM_DISCOUNT">{t('offers.ITEM_DISCOUNT')}</option>
          <option value="ANNOUNCEMENT">{t('offers.ANNOUNCEMENT')}</option>
        </Select>
      </Field>

      {typeChanged && offer.items.length > 0 && (
        <Alert tone="warning">{t('errors.TYPE_CHANGE_CLEARS_ITEMS')}</Alert>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('offers.startsAt')} htmlFor="offer-start">
          <Input
            id="offer-start"
            type="datetime-local"
            dir="ltr"
            disabled={!canEdit}
            value={startsAt}
            onChange={(event) => setStartsAt(event.target.value)}
          />
        </Field>
        <Field label={t('offers.endsAt')} htmlFor="offer-end" hint={t('offers.windowHint')}>
          <Input
            id="offer-end"
            type="datetime-local"
            dir="ltr"
            disabled={!canEdit}
            value={endsAt}
            onChange={(event) => setEndsAt(event.target.value)}
          />
        </Field>
      </div>

      <OptionalBilingualField
        label={t('offers.description')}
        value={description}
        onChange={setDescription}
        multiline
        maxLength={1000}
        disabled={!canEdit}
      />

      <OptionalBilingualField
        label={t('offers.terms')}
        value={terms}
        onChange={setTerms}
        multiline
        maxLength={1000}
        disabled={!canEdit}
      />

      <ImagePicker
        label={t('offers.image')}
        surface="offer"
        value={image}
        disabled={!canEdit}
        onChange={(picked) => {
          setImageId(picked?.id ?? null);
          setImage(picked ? { ...picked, storageKey: '', alt: { ar: '', en: '' } } : null);
        }}
      />

      {offer && offerType === 'ITEM_DISCOUNT' && <OfferItems offer={offer} canEdit={canEdit} />}
    </FormPage>
  );
}

/**
 * Which dishes are discounted, and to what.
 *
 * The list is sent whole — the API replaces the assignment rather than merging
 * it, so removing a row here removes it from the offer.
 */
function OfferItems({ offer, canEdit }: { offer: AdminOffer; canEdit: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const [rows, setRows] = useState(
    offer.items.map((item) => ({ itemId: item.id, promotionalPrice: item.promotionalPrice ?? '' })),
  );

  const catalogue = useQuery({
    queryKey: qk.items({ all: true }),
    queryFn: () => apiGet<AdminItem[]>('/admin/menu/items', { page: 1, pageSize: 200 }),
  });

  const assign = useAdminMutation(
    () =>
      apiPut(`${URL}/${offer.id}/items`, {
        expectedVersion: offer.version,
        items: rows.map((row) => ({
          itemId: row.itemId,
          promotionalPrice: row.promotionalPrice.trim() === '' ? undefined : row.promotionalPrice.trim(),
        })),
      }),
    { successMessage: t('common.saved'), invalidate: INVALIDATE },
  );

  const available = (catalogue.data?.data ?? []).filter(
    (item) => !rows.some((row) => row.itemId === item.id),
  );

  return (
    <div className="dio-field">
      <span className="dio-label">{t('offers.items')}</span>

      {rows.length === 0 ? (
        <p className="dio-hint">{t('offers.emptyItems')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((row) => {
            const item = (catalogue.data?.data ?? []).find((candidate) => candidate.id === row.itemId);
            const offerItem = offer.items.find((candidate) => candidate.id === row.itemId);
            const regular = item?.basePrice ?? offerItem?.regularPrice ?? null;
            const percent = discountPercent(regular, row.promotionalPrice || null);

            return (
              <li key={row.itemId} className="flex flex-wrap items-center gap-2">
                <span className="min-w-0 flex-1">
                  {item
                    ? item.name[locale] || item.name.ar || item.name.en
                    : offerItem?.name[locale] || row.itemId}
                </span>

                <span className="dio-meta tabular">
                  <Money amount={regular} fallback={t('items.noPrice')} />
                </span>

                <Input
                  type="text"
                  inputMode="decimal"
                  dir="ltr"
                  className="tabular"
                  style={{ inlineSize: '7rem' }}
                  aria-label={t('offers.promotionalPrice')}
                  disabled={!canEdit}
                  value={row.promotionalPrice}
                  onChange={(event) =>
                    setRows((current) =>
                      current.map((candidate) =>
                        candidate.itemId === row.itemId
                          ? { ...candidate, promotionalPrice: event.target.value }
                          : candidate,
                      ),
                    )
                  }
                />

                {percent !== null && <Badge tone="success">{t('offers.saving', { percent })}</Badge>}
                {offerItem && !offerItem.publiclyVisible && (
                  <Badge tone="warning" dot>
                    {t('offers.notVisible')}
                  </Badge>
                )}

                {canEdit && (
                  <Button
                    size="sm"
                    iconOnly
                    variant="ghost"
                    aria-label={t('common.remove')}
                    onClick={() => setRows((current) => current.filter((c) => c.itemId !== row.itemId))}
                  >
                    <Trash2 className="dio-icon" aria-hidden="true" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {canEdit && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Select
            value=""
            aria-label={t('offers.addItem')}
            onChange={(event) => {
              if (!event.target.value) return;
              setRows((current) => [...current, { itemId: event.target.value, promotionalPrice: '' }]);
            }}
          >
            <option value="">{t('offers.addItem')}</option>
            {available.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name[locale] || item.name.ar || item.name.en}
              </option>
            ))}
          </Select>

          <Button size="sm" loading={assign.isPending} onClick={() => assign.mutate(undefined)}>
            {t('common.save')}
          </Button>
        </div>
      )}

      <p className="dio-hint mt-2">{t('offers.expiredNote')}</p>
    </div>
  );
}
